/**
 * FlashBook loudness — what a page actually sounds like, and how to make every
 * page sound like the same book.
 *
 * The problem this solves is the one a creator *hears* rather than sees: page 4
 * was recorded in a quiet room and page 5 in a kitchen, so the learner reaches
 * for the volume on every turn of the page. Matching *peaks* cannot fix that — two
 * takes can share a peak and still be eight decibels apart in how loud they read,
 * because loudness is about energy over time and about the frequencies the ear is
 * most sensitive to, not about the one tallest sample.
 *
 * So this module measures loudness the way broadcast does — ITU-R BS.1770-4 /
 * EBU R128, implemented here rather than approximated:
 *
 *  1. **K-weighting.** The signal is filtered to the ear's own response: a
 *     high-shelf for the head's acoustic gain, then a high-pass for the fact that
 *     we barely hear anything under 40 Hz. The coefficients are *derived* from the
 *     sample rate rather than copied from the standard's 48 kHz table, because a
 *     phone records at 44.1 kHz and a table designed for another rate is a
 *     different filter.
 *  2. **Gated blocks.** Loudness is averaged over 400 ms windows with 75 %
 *     overlap, and two gates throw away the parts that are not the recording:
 *     an absolute gate at −70 LUFS (silence, and the noise floor of an empty
 *     room) and a relative gate 10 LU below the programme's own mean (pauses,
 *     breaths, the gap between two sentences). Without gating, a page with four
 *     seconds of silence in the middle measures quieter than the same take
 *     without them — which is precisely the wrong answer.
 *
 * Everything here is a pure function of samples: no DOM, no audio context, no
 * engine. That is deliberate — the measurement is the part that has to be
 * *right*, so it is the part that can be tested against known values.
 *
 * The planner is the other half. It answers "what should happen to this book"
 * without touching a byte: which clips change, by how much, where they land, and
 * which ones cannot reach the target at all because doing so would clip.
 *
 * Two features are built on it, which is why the ceiling and the boost cap live
 * here rather than in either of them: matching a whole book to its own median, and
 * mastering a single page on its own (`flashbookAutoMaster`). A page fixed alone
 * and a page fixed with its neighbours have to land in the same place, or a
 * creator polishing one page would silently break the book it belongs to.
 */

/** The clip surface this module needs — satisfied by an `AudioBuffer`. */
export type LoudnessSource = {
  numberOfChannels: number
  length: number
  sampleRate: number
  getChannelData: (channel: number) => Float32Array
}

// ─── The constants of the standard ────────────────────────────────────────────

/**
 * The offset in the gating equation.
 *
 * `-0.691` is not a magic number: it is what makes a 1 kHz sine at −20 dBFS
 * measure as −20 LUFS, i.e. it is what fixes the *scale*. Change it and every
 * number here becomes a different unit.
 */
const LOUDNESS_OFFSET = -0.691

/** Blocks below this are treated as silence and excluded outright. */
const ABSOLUTE_GATE_LUFS = -70

/** Blocks below the programme's own mean by this much are excluded. */
const RELATIVE_GATE_LU = 10

/** The window a loudness figure is averaged over. */
const BLOCK_SECONDS = 0.4

/** How far the window advances. A quarter of it, so blocks overlap 75 %. */
const BLOCK_STEP_SECONDS = 0.1

/** A linear sample value's floor in dB, so silence is finite rather than `-Inf`. */
const SILENCE_DB = -120

// ─── K-weighting ─────────────────────────────────────────────────────────────

/** A direct-form biquad, normalised so `a0` is 1. */
type Biquad = {
  b0: number
  b1: number
  b2: number
  a1: number
  a2: number
}

/**
 * The two filters of K-weighting, designed for a given sample rate.
 *
 * Both come from the analytic design the standard's tables are a 48 kHz
 * instantiation of: the shelf from the gain/Q/corner triple, the high-pass from
 * its corner and Q. Deriving them means 44.1 kHz gets a real K-weighting filter
 * instead of a 48 kHz one applied 8 % fast.
 */
export function designKWeighting(sampleRate: number): [Biquad, Biquad] {
  // Stage 1 — the head's acoustic gain: a high shelf, +4 dB above ~1.7 kHz.
  const shelfGainDb = 3.999843853973347
  const shelfQ = 0.7071752369554196
  const shelfFc = 1681.974450955533
  const k1 = Math.tan((Math.PI * shelfFc) / sampleRate)
  const vh = Math.pow(10, shelfGainDb / 20)
  const vb = Math.pow(vh, 0.4996667741545416)
  const shelfA0 = 1 + k1 / shelfQ + k1 * k1
  const shelf: Biquad = {
    b0: (vh + (vb * k1) / shelfQ + k1 * k1) / shelfA0,
    b1: (2 * (k1 * k1 - vh)) / shelfA0,
    b2: (vh - (vb * k1) / shelfQ + k1 * k1) / shelfA0,
    a1: (2 * (k1 * k1 - 1)) / shelfA0,
    a2: (1 - k1 / shelfQ + k1 * k1) / shelfA0,
  }

  // Stage 2 — the ear's own roll-off below ~38 Hz.
  const highPassFc = 38.13547087602444
  const highPassQ = 0.5003270373238773
  const k2 = Math.tan((Math.PI * highPassFc) / sampleRate)
  const highPassA0 = 1 + k2 / highPassQ + k2 * k2
  const highPass: Biquad = {
    b0: 1,
    b1: -2,
    b2: 1,
    a1: (2 * (k2 * k2 - 1)) / highPassA0,
    a2: (1 - k2 / highPassQ + k2 * k2) / highPassA0,
  }

  return [shelf, highPass]
}

/** Runs one biquad over a signal, in place. */
function applyBiquad(data: Float32Array, filter: Biquad): void {
  let x1 = 0
  let x2 = 0
  let y1 = 0
  let y2 = 0
  for (let i = 0; i < data.length; i++) {
    const x0 = data[i]
    const y0 =
      filter.b0 * x0 +
      filter.b1 * x1 +
      filter.b2 * x2 -
      filter.a1 * y1 -
      filter.a2 * y2
    x2 = x1
    x1 = x0
    y2 = y1
    y1 = y0
    data[i] = y0
  }
}

/**
 * The K-weighted channels of a clip, as copies.
 *
 * Copies, because `getChannelData` hands back the buffer's own storage and the
 * caller's clip must come out of a measurement exactly as it went in — a
 * measurement that quietly filtered the take it was asked about would be a
 * measurement that changed the book.
 */
export function kWeightedChannels(source: LoudnessSource): Float32Array[] {
  const channels = Math.max(
    1,
    Math.min(2, Math.round(source.numberOfChannels || 1)),
  )
  const [shelf, highPass] = designKWeighting(source.sampleRate)
  const out: Float32Array[] = []
  for (let c = 0; c < channels; c++) {
    const from = source.getChannelData(
      Math.min(c, Math.max(0, source.numberOfChannels - 1)),
    )
    const data = new Float32Array(from.length)
    data.set(from)
    applyBiquad(data, shelf)
    applyBiquad(data, highPass)
    out.push(data)
  }
  return out
}

// ─── Measurement ─────────────────────────────────────────────────────────────

/** A linear sample value as dBFS, floored so silence has a number. */
export function linearToDb(value: number): number {
  if (!(value > 0)) return SILENCE_DB
  return Math.max(SILENCE_DB, 20 * Math.log10(value))
}

/**
 * What one clip measures.
 *
 * `lufs` is `null` rather than `-Infinity` when there is nothing to measure:
 * a clip of digital silence has no loudness, and pretending it has one would let
 * the planner "match" it by amplifying noise. `peakDb` is the loudest *sample*,
 * which is what the ceiling is enforced against.
 */
export type LoudnessMeasurement = {
  /** Integrated loudness, in LUFS. `null` when the clip is silence. */
  lufs: number | null
  /** The loudest sample, in dBFS. `null` when the clip is silence. */
  peakDb: number | null
  durationSec: number
  /** Blocks the gates admitted; 0 means every window was below −70 LUFS. */
  gatedBlocks: number
  /** Blocks analysed at all — the clip's length, in windows. */
  totalBlocks: number
}

/**
 * The integrated loudness of a clip, per BS.1770.
 *
 * The block mean-squares are computed once and then gated twice, because the
 * second gate's threshold depends on the first gate's mean — that ordering is
 * the standard, not an implementation detail.
 */
export function measureLoudness(source: LoudnessSource): LoudnessMeasurement {
  const sampleRate = source.sampleRate || 48000
  const channels = kWeightedChannels(source)
  const frames = channels[0]?.length ?? 0
  const durationSec = frames / sampleRate

  // The peak is read from the *unfiltered* samples: K-weighting exists to model
  // how loud something sounds, and a ceiling is about what the DAC is handed.
  let peak = 0
  const rawChannels = Math.max(
    1,
    Math.min(2, Math.round(source.numberOfChannels || 1)),
  )
  for (let c = 0; c < rawChannels; c++) {
    const data = source.getChannelData(
      Math.min(c, Math.max(0, source.numberOfChannels - 1)),
    )
    for (let i = 0; i < data.length; i++) {
      const a = Math.abs(data[i])
      if (a > peak) peak = a
    }
  }
  const peakDb = peak > 0 ? linearToDb(peak) : null

  if (frames === 0) {
    return {lufs: null, peakDb, durationSec: 0, gatedBlocks: 0, totalBlocks: 0}
  }

  const blockLength = Math.max(1, Math.round(BLOCK_SECONDS * sampleRate))
  const step = Math.max(1, Math.round(BLOCK_STEP_SECONDS * sampleRate))

  /*
   * A clip shorter than one block is analysed as a single block covering all of
   * it. Refusing to measure a half-second take would be the worst possible
   * behaviour here: a one-word reading is exactly the clip most likely to be
   * out of step with the rest of the book.
   */
  const starts: number[] = []
  if (frames <= blockLength) {
    starts.push(0)
  } else {
    for (let start = 0; start + blockLength <= frames; start += step) {
      starts.push(start)
    }
  }

  // The mean square of each block, summed across channels with weight 1 — the
  // stereo and mono case of the standard, which is every clip a FlashBook holds.
  const blockEnergy: number[] = []
  for (const start of starts) {
    const end = Math.min(frames, start + blockLength)
    const span = end - start
    let sum = 0
    for (const channel of channels) {
      let square = 0
      for (let i = start; i < end; i++) {
        square += channel[i] * channel[i]
      }
      sum += square / span
    }
    blockEnergy.push(sum)
  }

  const loudnessOf = (energy: number) =>
    energy > 0 ? LOUDNESS_OFFSET + 10 * Math.log10(energy) : -Infinity

  // Gate 1 — absolute. Silence, room tone and digital black leave here.
  const aboveAbsolute = blockEnergy.filter(
    energy => loudnessOf(energy) > ABSOLUTE_GATE_LUFS,
  )
  if (aboveAbsolute.length === 0) {
    return {
      lufs: null,
      peakDb,
      durationSec,
      gatedBlocks: 0,
      totalBlocks: blockEnergy.length,
    }
  }

  // Gate 2 — relative to the programme's own mean, so pauses inside a take do
  // not drag the figure down.
  const meanOfAbsolute =
    aboveAbsolute.reduce((total, energy) => total + energy, 0) /
    aboveAbsolute.length
  const relativeGate =
    LOUDNESS_OFFSET + 10 * Math.log10(meanOfAbsolute) - RELATIVE_GATE_LU
  const gated = aboveAbsolute.filter(
    energy => loudnessOf(energy) > relativeGate,
  )

  // `gated` cannot be empty: every member of `aboveAbsolute` clears its own mean
  // minus 10 LU by construction. Kept as a guard rather than a belief.
  if (gated.length === 0) {
    return {
      lufs: null,
      peakDb,
      durationSec,
      gatedBlocks: 0,
      totalBlocks: blockEnergy.length,
    }
  }

  const meanEnergy =
    gated.reduce((total, energy) => total + energy, 0) / gated.length
  return {
    lufs: LOUDNESS_OFFSET + 10 * Math.log10(meanEnergy),
    peakDb,
    durationSec,
    gatedBlocks: gated.length,
    totalBlocks: blockEnergy.length,
  }
}

// ─── Planning ────────────────────────────────────────────────────────────────

/** A measured clip, as the planner sees it. */
export type MeasuredLevel = {
  id: string
  measurement: LoudnessMeasurement
}

/**
 * The peak a matched clip is allowed to reach.
 *
 * −1 dBFS rather than −0.1, and the gap is not decoration. Sample-accurate
 * measurement cannot see *inter-sample* peaks, which a lossy re-encode can turn
 * back into real ones, so a clip sitting at full scale after matching can clip
 * on the listener's device while measuring clean here.
 */
export const LEVEL_CEILING_DB = -1

/**
 * How close is close enough, in LU.
 *
 * A half-lu is below the threshold of a careful listener and well below the
 * several-lu gap that makes a book sound uneven, so it is the right place to
 * stop: it makes a second run of the same book a no-op instead of a slow drift.
 */
export const LEVEL_TOLERANCE_LU = 0.5

/**
 * The most a page may be lifted, in dB.
 *
 * Past this the recording is mostly room tone and mic self-noise, so raising it
 * to the target does not make the page *louder*, it makes the room louder — the
 * opposite of matching the book. Such a clip is reported rather than mangled.
 */
export const LEVEL_MAX_BOOST_DB = 15

export type LevelMatchOptions = {
  /** Leave a clip alone when the change is smaller than this, in LU. */
  toleranceLu?: number
  /** The peak a rewritten clip must not exceed, in dBFS. */
  ceilingDb?: number
  /** The largest lift allowed, in dB. */
  maxBoostDb?: number
}

/** Why a clip did not land exactly on the target. */
export type LevelLimit = 'target' | 'peak' | 'gain'

export type LevelChange = {
  id: string
  /** What it measures now. */
  lufs: number
  /** How much it is moved, in dB. Negative is a cut. */
  gainDb: number
  /** Where it is predicted to land. */
  predictedLufs: number
  /**
   * `target` — it lands on the target. `peak` — the ceiling stopped it short.
   * `gain` — the lift was capped, because the page is mostly room tone.
   */
  limitedBy: LevelLimit
}

export type LevelSpread = {
  min: number
  max: number
  /** `max - min`, in LU. The number a creator actually cares about. */
  spread: number
}

export type LevelMatchPlan = {
  /** The level every clip is brought to, in LUFS. `null` when nothing measured. */
  targetLufs: number | null
  /** Clips that will be rewritten, in input order. */
  changes: LevelChange[]
  /** Clips already close enough, left exactly as they are. */
  settled: string[]
  /** Clips with nothing to measure — digital silence. */
  silent: string[]
  /** The book's spread now, and where it lands after. */
  spreadBefore: LevelSpread | null
  spreadAfter: LevelSpread | null
  /** How many clips land within tolerance of the target. */
  reachedTarget: number
  /** Whether the plan would change anything at all. */
  isEmpty: boolean
}

function spreadOf(levels: number[]): LevelSpread | null {
  if (levels.length === 0) return null
  let min = levels[0]
  let max = levels[0]
  for (const level of levels) {
    if (level < min) min = level
    if (level > max) max = level
  }
  return {min, max, spread: max - min}
}

/**
 * The target a book should be brought to, when the creator has not named one.
 *
 * The **median** of the clips that have a loudness, deliberately, rather than a
 * broadcast standard like −16 LUFS. A creator recorded this book at a level they
 * chose, and matching to an absolute figure would move the whole book — every
 * page louder or every page quieter — which is not the complaint. The complaint
 * is that the pages disagree. The median collapses the disagreement while
 * leaving half the book untouched and the book's overall level exactly where its
 * author put it.
 */
export function bookTargetLufs(levels: MeasuredLevel[]): number | null {
  const measurable = levels
    .map(level => level.measurement.lufs)
    .filter((lufs): lufs is number => lufs !== null)
    .sort((a, b) => a - b)
  if (measurable.length === 0) return null
  const middle = Math.floor(measurable.length / 2)
  return measurable.length % 2 === 1
    ? measurable[middle]
    : (measurable[middle - 1] + measurable[middle]) / 2
}

/**
 * The gain that brings one clip to the target — and the reason it may not.
 *
 * The ceiling is allowed to win over the target, always: a page that cannot be
 * as loud as the rest without distorting is a page that stays quiet and says so,
 * because the alternative is a page that clips. The one case where the two agree
 * is a clip that is both above the target and above the ceiling, where the cut
 * the ceiling demands is also a cut the target wants.
 */
export function plannedGain(
  measurement: LoudnessMeasurement,
  targetLufs: number,
  options: LevelMatchOptions = {},
): {gainDb: number; limitedBy: LevelLimit} | null {
  if (measurement.lufs === null) return null
  const ceilingDb = options.ceilingDb ?? LEVEL_CEILING_DB
  const maxBoostDb = options.maxBoostDb ?? LEVEL_MAX_BOOST_DB

  const ideal = targetLufs - measurement.lufs

  // No measurable peak means no way to know what the ceiling is, so nothing is
  // lifted. A clip in that state is one the gates let through while every sample
  // is zero, which is not a thing to amplify on a guess.
  if (measurement.peakDb === null) return {gainDb: ideal, limitedBy: 'target'}

  const roomToTheCeiling = ceilingDb - measurement.peakDb
  if (ideal > roomToTheCeiling) {
    return {gainDb: roomToTheCeiling, limitedBy: 'peak'}
  }
  if (ideal > maxBoostDb) {
    return {gainDb: maxBoostDb, limitedBy: 'gain'}
  }
  return {gainDb: ideal, limitedBy: 'target'}
}

/**
 * What to do to a whole book, decided without writing a byte.
 *
 * Split from applying it on purpose: a creator gets to see that page 7 is about
 * to be lifted 9 dB before it is, and a test gets to check the arithmetic
 * without a sound card. The predicted spread is computed from the gains that
 * will *actually* be applied — including the ones the ceiling shortened — so the
 * dialog never promises a book is level when it is not.
 */
export function planLevelMatch(
  levels: MeasuredLevel[],
  targetLufs: number | null,
  options: LevelMatchOptions = {},
): LevelMatchPlan {
  const toleranceLu = options.toleranceLu ?? LEVEL_TOLERANCE_LU
  const measurable = levels.filter(level => level.measurement.lufs !== null)
  const silent = levels
    .filter(level => level.measurement.lufs === null)
    .map(level => level.id)

  const spreadBefore = spreadOf(
    measurable.map(level => level.measurement.lufs as number),
  )

  if (targetLufs === null || measurable.length === 0) {
    return {
      targetLufs: null,
      changes: [],
      settled: [],
      silent,
      spreadBefore,
      spreadAfter: spreadBefore,
      reachedTarget: 0,
      isEmpty: true,
    }
  }

  const changes: LevelChange[] = []
  const settled: string[] = []
  const landed: number[] = []
  let reachedTarget = 0

  for (const level of levels) {
    const lufs = level.measurement.lufs
    if (lufs === null) continue
    const planned = plannedGain(level.measurement, targetLufs, options)
    if (!planned) continue

    if (Math.abs(planned.gainDb) <= toleranceLu) {
      settled.push(level.id)
      landed.push(lufs)
      reachedTarget++
      continue
    }

    const predictedLufs = lufs + planned.gainDb
    landed.push(predictedLufs)
    if (Math.abs(predictedLufs - targetLufs) <= toleranceLu) reachedTarget++
    changes.push({
      id: level.id,
      lufs,
      gainDb: planned.gainDb,
      predictedLufs,
      limitedBy: planned.limitedBy,
    })
  }

  return {
    targetLufs,
    changes,
    settled,
    silent,
    spreadBefore,
    spreadAfter: spreadOf(landed),
    reachedTarget,
    isEmpty: changes.length === 0,
  }
}

// ─── Presentation ────────────────────────────────────────────────────────────

/** A loudness figure as `−18.4 LUFS`, using a real minus sign. */
export function formatLufs(lufs: number): string {
  return `${lufs < 0 ? '−' : ''}${Math.abs(lufs).toFixed(1)} LUFS`
}

/** A gain as `+3.2 dB` / `−4.0 dB`, with the sign always shown. */
export function formatGain(db: number): string {
  const sign = db < 0 ? '−' : '+'
  return `${sign}${Math.abs(db).toFixed(1)} dB`
}

/** A spread as `6.4 LU`. */
export function formatSpread(spread: number): string {
  return `${spread.toFixed(1)} LU`
}
